import { useState, useEffect, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  Megaphone,
  Mail,
  Linkedin,
} from "lucide-react";
import { useContactDetailFull } from "@/hooks/useContactDetail";
import {
  useContactCampaigns,
  useContactOpportunities,
} from "@/hooks/useContactIntelligence";
import { EngagementStatusBadge } from "@/components/campaign/EngagementStatusBadge";
import { ContactHeader } from "./ContactHeader";
import { ContactContext } from "./ContactContext";
import { ContactIntelTab } from "./ContactIntelTab";
import { ContactProfileTab } from "./ContactProfileTab";
import { ContactActivityTab } from "./ContactActivityTab";

// ── Types ────────────────────────────────────────────────────────────

export type ContactModalMode = "default" | "email" | "linkedin";

export interface UnifiedContactModalProps {
  contactId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: ContactModalMode;
  campaignId?: string | null;
  campaignName?: string | null;
  panel?: ReactNode;
  fallbackData?: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    job_title?: string | null;
    company_name?: string | null;
    email?: string | null;
    linkedin_url?: string | null;
    location?: string | null;
    headline?: string | null;
  } | null;
}

// ── Component ────────────────────────────────────────────────────────

export function UnifiedContactModal({
  contactId,
  open,
  onOpenChange,
  mode = "default",
  campaignId,
  campaignName,
  panel,
  fallbackData,
}: UnifiedContactModalProps) {
  const { data: contact, isLoading } = useContactDetailFull(open ? contactId : null);
  const { data: campaigns } = useContactCampaigns(open ? contactId : null);
  const { data: opportunities } = useContactOpportunities(open ? contactId : null);

  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    if (open) setActiveTab("profile");
  }, [open]);

  const isUnmatched = !contactId || (!isLoading && !contact);
  const hasPanel = mode !== "default" && !!panel;

  // Surface the contact's engagement status in the comms header: the campaign
  // the modal was opened from, else the most recent.
  const currentCampaign =
    (campaignId ? campaigns?.find((c) => c.campaign_id === campaignId) : null) ?? campaigns?.[0] ?? null;

  // Comms mode opens a near-viewport workspace so the dual-pane has room.
  const widthClass = hasPanel ? "w-[96vw] max-w-[1400px]" : "max-w-5xl";

  // Compact-header identity (comms mode). Mirrors ContactHeader resolution so
  // unmatched contacts (fallbackData, no contacts row) still render.
  const displayName =
    contact?.full_name ||
    fallbackData?.full_name ||
    [fallbackData?.first_name, fallbackData?.last_name].filter(Boolean).join(" ") ||
    "Contact";
  const displayTitle = contact?.job_title || fallbackData?.job_title;
  const displayCompany = contact?.company_name || fallbackData?.company_name;
  const displayEmail = contact?.email || fallbackData?.email;
  const displayLinkedIn = contact?.linkedin_url || fallbackData?.linkedin_url;
  const pictureUrl = (contact as { clay_picture_url?: string | null } | null)?.clay_picture_url || null;
  const initials = (displayName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Read-only (non-comms) body: Profile (identity + intel folded in) + Activity
  // (campaign results + opportunities, NO message bodies).
  const renderContactDetail = () => {
    if (isUnmatched) {
      return (
        <div className="flex-1 overflow-auto p-6">
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <User className="h-10 w-10 mb-3" />
            <p className="text-sm font-medium">Unmatched Contact</p>
            <p className="text-xs mt-1 text-center max-w-sm">
              This contact hasn't been matched to a contact record yet. Only basic
              campaign data is available.
            </p>
          </div>
        </div>
      );
    }
    if (!contact) return null;
    return (
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-6 mt-2 w-fit">
          <TabsTrigger value="profile" className="text-sm">
            <User className="h-3.5 w-3.5 mr-1.5" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="activity" className="text-sm">
            <Megaphone className="h-3.5 w-3.5 mr-1.5" />
            Activity
            {(campaigns?.length ?? 0) > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-[11px] px-1.5 py-0">
                {campaigns?.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 min-h-0 overflow-hidden">
          <TabsContent value="profile" className="mt-0 h-full">
            <ScrollArea className="h-full">
              <div className="px-6 pb-6 pt-4 space-y-4">
                <ContactProfileTab contact={contact} />
                <Separator />
                <ContactIntelTab contact={contact} />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="activity" className="mt-0 h-full">
            <ScrollArea className="h-full">
              <div className="px-6 pb-6 pt-4">
                <ContactActivityTab campaigns={campaigns} opportunities={opportunities} />
              </div>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${widthClass} max-h-[92vh] flex flex-col p-0 overflow-hidden`}>
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <div className="shrink-0">
              {hasPanel ? (
                /* Comms mode: one slim identity row so the dual-pane fills the
                   modal. No Account Context, no tall header, no tabs.
                   pr-12 keeps the right-side chips clear of the dialog close X. */
                <div className="flex items-center gap-3 px-5 py-2.5 pr-12 border-b">
                  <Avatar className="h-8 w-8 shrink-0">
                    {pictureUrl && <AvatarImage src={pictureUrl} alt={displayName} />}
                    <AvatarFallback className="text-[11px] bg-primary/10 text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1 flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold truncate">{displayName}</span>
                    {(displayTitle || displayCompany) && (
                      <span className="text-xs text-muted-foreground truncate">
                        · {[displayTitle, displayCompany].filter(Boolean).join(" at ")}
                      </span>
                    )}
                  </div>

                  {currentCampaign && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <EngagementStatusBadge status={currentCampaign.engagement_status} />
                      {currentCampaign.reply_sentiment && (
                        <Badge variant="secondary" className="text-[11px] h-5 px-1.5">
                          {currentCampaign.reply_sentiment}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 shrink-0">
                    {displayEmail && (
                      <a
                        href={`mailto:${displayEmail}`}
                        title={displayEmail}
                        onClick={(e) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    {displayLinkedIn && (
                      <a
                        href={displayLinkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="LinkedIn"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <ContactHeader contact={contact ?? null} fallbackData={fallbackData} />
                  {contact && <ContactContext contact={contact} />}
                </>
              )}
            </div>

            {/* Main body */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {hasPanel ? (
                /* Comms mode: the message + context dual-pane fills the modal.
                   overflow-hidden so each pane (left context / right message)
                   scrolls independently — the panel owns its own scroll regions. */
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-muted/5">
                  {panel}
                </div>
              ) : (
                renderContactDetail()
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
